import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Compass, GraduationCap, ShieldAlert, TrendingDown, LogOut, Sparkles } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import GuidedTour, { TourTrigger } from "./GuidedTour";

const NAV = {
  employee: [
    { to: "/employee", icon: LayoutDashboard, label: "Genel Bakış" },
    { to: "/employee/skills", icon: Compass, label: "Yetkinlik Profili" },
    { to: "/employee/career", icon: TrendingDown, label: "Kariyer Yolu" },
    { to: "/employee/mentors", icon: GraduationCap, label: "Mentor Önerileri" },
  ],
  manager: [
    { to: "/manager", icon: LayoutDashboard, label: "Takım Görünümü" },
    { to: "/manager/attrition", icon: TrendingDown, label: "Ayrılma Riski" },
    { to: "/manager/succession", icon: ShieldAlert, label: "Yedekleme" },
  ],
  hr: [
    { to: "/hr", icon: LayoutDashboard, label: "İK Panosu" },
    { to: "/hr/talent-pool", icon: Users, label: "Yetenek Havuzu" },
    { to: "/hr/succession", icon: ShieldAlert, label: "Kritik Roller" },
    { to: "/hr/attrition", icon: TrendingDown, label: "Ayrılma Riski" },
  ],
};

export const Layout = ({ persona, children }) => {
  const loc = useLocation();
  const nav = useNavigate();
  const items = NAV[persona?.role] || [];

  const handleSwitch = () => {
    sessionStorage.removeItem("plena_persona");
    nav("/personas");
  };

  const handleLogout = () => {
    sessionStorage.removeItem("plena_persona");
    sessionStorage.removeItem("plena_session_mode");
    const tenantRaw = sessionStorage.getItem("plena_tenant");
    sessionStorage.removeItem("plena_tenant");
    if (tenantRaw) {
      try {
        const t = JSON.parse(tenantRaw);
        nav(`/demo/${t.slug}`);
        return;
      } catch (e) { /* ignore */ }
    }
    nav("/");
  };

  // Tenant branding (if customer demo session)
  const tenant = (() => {
    try { return JSON.parse(sessionStorage.getItem("plena_tenant") || "null"); } catch { return null; }
  })();
  const brand = tenant?.brand_color || "#0EA5E9";

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-72 shrink-0 border-r border-slate-200 bg-white/85 backdrop-blur-xl flex flex-col">
        <div className="p-7 border-b border-slate-200">
          <Link to="/personas" className="flex items-center gap-3 group" data-testid="brand-link">
            {tenant?.logo_url ? (
              <>
                <img src={tenant.logo_url} alt={tenant.name} className="h-10 max-w-[150px] object-contain" />
                <div className="text-[0.7rem] text-slate-500 tracking-wide leading-tight border-l border-slate-200 pl-3">
                  Plena Growth<br />
                  <span className="text-slate-400">Yetenek Zekası</span>
                </div>
              </>
            ) : (
              <>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center shadow-lg" style={{ background: `linear-gradient(135deg, ${brand}, ${brand}cc)`, boxShadow: `0 8px 20px -8px ${brand}aa` }}>
                  <Sparkles className="w-5 h-5 text-white" strokeWidth={2.2} />
                </div>
                <div>
                  <div className="font-display text-xl font-semibold leading-none tracking-tight">
                    {tenant ? tenant.name : <>Plena <span style={{ color: brand }}>Growth</span></>}
                  </div>
                  <div className="text-[0.7rem] text-slate-500 mt-1.5 tracking-wide">
                    {tenant ? "Plena Growth · Yetenek Zekası" : "Yetenek Zekası"}
                  </div>
                </div>
              </>
            )}
          </Link>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1.5">
          <div className="label-eyebrow px-3 mb-3">Modüller</div>
          {items.map((item) => {
            const active = loc.pathname === item.to || (item.to !== `/${persona.role}` && loc.pathname.startsWith(item.to));
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.replace(/\//g, "-")}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-[0.92rem] transition-all ${
                  active
                    ? "bg-sky-50 text-sky-800 border border-sky-200 shadow-sm"
                    : "text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-transparent"
                }`}
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={active ? 2.2 : 1.8} />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-5 border-t border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="w-11 h-11 ring-2 ring-sky-200">
              <AvatarImage src={persona?.avatar} alt={persona?.name} />
              <AvatarFallback className="bg-slate-100 text-slate-800 text-sm">
                {persona?.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{persona?.name}</div>
              <div className="text-xs text-slate-500 truncate">{persona?.title}</div>
            </div>
          </div>
          <button
            data-testid="switch-persona-btn"
            onClick={handleSwitch}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-xs font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition border border-slate-200"
          >
            <LogOut className="w-3.5 h-3.5" /> Persona değiştir
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        {/* Top bar with integrations + tour */}
        <div className="border-b border-slate-200 bg-white/70 backdrop-blur-xl">
          <div className="max-w-[1500px] mx-auto px-10 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 text-[0.7rem] text-slate-500">
              <Avatar className="w-7 h-7 ring-1 ring-slate-200">
                <AvatarImage src={persona?.avatar} alt={persona?.name} />
                <AvatarFallback className="bg-slate-100 text-slate-700 text-[0.65rem]">
                  {persona?.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="leading-tight">
                <div className="text-[0.78rem] font-semibold text-slate-800">{persona?.name}</div>
                <div className="text-[0.65rem] text-slate-500">{persona?.title}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <TourTrigger scope="dashboard" label="Demo Turu" />
              <button
                type="button"
                data-testid="top-logout-btn"
                onClick={handleLogout}
                className="inline-flex items-center gap-1.5 text-[0.7rem] font-medium text-slate-600 hover:text-rose-600 px-2.5 py-1.5 rounded-md border border-slate-200 hover:border-rose-200 hover:bg-rose-50/60 transition"
                title="Oturumu kapat"
              >
                <LogOut className="w-3 h-3" />
                Çıkış
              </button>
            </div>
          </div>
        </div>
        <div className="max-w-[1500px] mx-auto px-10 py-10">{children}</div>
        <GuidedTour scope="dashboard" autoStart={false} />
      </main>
    </div>
  );
};

export default Layout;
